import React from 'react';
import { Button, useToast, Checkbox } from '@crisellevaldez/speculo-ui';
import { ExternalLink, Copy } from 'lucide-react';
import { InfoNotice } from './InfoNotice';
import { SUPPORT_CONTACT } from '../../../../constants/supportContact';

interface GoDaddyDelegateAccessProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  checkboxLabel: string;
  error?: string;
}

/** How to give Speculo domain-only (delegate) access in GoDaddy, plus a confirmation box. */
export const GoDaddyDelegateAccess: React.FC<GoDaddyDelegateAccessProps> = ({
  checked,
  onCheckedChange,
  checkboxLabel,
  error,
}) => {
  const { showToast } = useToast();

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showToast({ message: `${label} copied to clipboard`, variant: 'success' });
  };

  const shareWith = [
    { label: 'Name', value: 'Speculo' },
    { label: 'Email', value: SUPPORT_CONTACT.email },
  ];

  return (
    <InfoNotice>
      <div className="space-y-4">
        <div className="space-y-2">
          <p>
            You can give Speculo domain-only (delegate) access in GoDaddy without sharing your
            password. Send the invite to the details below.
          </p>
          <a
            href="https://www.loom.com/share/69b30ee489324de394a3c2894b0ada7a"
            className="text-blue-600 hover:underline font-semibold inline-flex items-center gap-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            Watch how to delegate GoDaddy access
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        <div className="pt-3 border-t border-blue-200">
          <p className="text-sm font-semibold mb-2">Invite:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {shareWith.map(({ label, value }) => (
              <div key={label} className="grid grid-cols-[50px_1fr_auto] items-center gap-2">
                <span className="font-semibold text-xs">{label}:</span>
                <code className="bg-white px-2 py-1 rounded text-xs truncate">{value}</code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(value, label)}
                  aria-label={`Copy ${label}`}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t border-blue-200">
            <div className="flex items-center gap-2">
              <Checkbox
                id="godaddy-access-shared"
                checked={checked}
                onChange={(e) => onCheckedChange(e.target.checked)}
                label={checkboxLabel}
              />
              <span className="text-red-600 dark:text-red-400 text-sm">*</span>
            </div>
            {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
          </div>
        </div>
      </div>
    </InfoNotice>
  );
};
